import React, {useState} from "react";
import {Line} from "react-chartjs-2";
import {
  ScriptableLineSegmentContext,
  ChartDataset,
  TooltipItem,
  Plugin,
} from "chart.js";
import {useTheme} from "../context/ThemeContext";

interface CustomLineData extends ChartDataset<"line", number[]> {
  segment?: {
    borderDash?: (ctx: ScriptableLineSegmentContext) => number[] | undefined;
    pointBorderWidth?: (ctx: ScriptableLineSegmentContext) => number | undefined;
    pointStyle?: (ctx: ScriptableLineSegmentContext) => string | undefined;
  };
}

// Modern color palette based on the design system
const colors = [
  "#3B82F6", // Primary chart blue
  "#02402c", // Success dark green
  "#059669", // Success green
  "#1E40AF", // Deep blue
  "#DC2626", // Error red
  "#9c1e1e", // Error dark red
  "#94A3B8", // Neutral warm gray (replaced orange)
];

interface Props {
  data: StockChartData;
  dataUpMax?: StockChartData;
  dataUpAvg?: StockChartData;
  dataDownMax?: StockChartData;
  dataDownAvg?: StockChartData;
  dataAllAvg?: StockChartData;
  // Median-analog projection cone (median line + 10th/90th band).
  dataMedian?: StockChartData;
  dataP10?: StockChartData;
  dataP90?: StockChartData;
  // Projection line labels hidden on first render (users toggle via the checkboxes).
  defaultHidden?: string[];
  // Number of sampled historical points before daily forecast points begin.
  // Kept separate from a match's real 65/130/260-session origin horizon.
  historicalPointCount?: number;
  isCompact?: boolean;
}

const ModernStockChart: React.FC<Props> = ({
  data,
  dataUpMax,
  dataUpAvg,
  dataDownMax,
  dataDownAvg,
  dataAllAvg,
  dataMedian,
  dataP10,
  dataP90,
  defaultHidden,
  historicalPointCount,
  isCompact = false,
}) => {
  const {theme} = useTheme();
  const isDark = theme === "dark";
  // Which projection lines are hidden. Seeded once from defaultHidden; toggled by
  // the custom checkbox legend below (kept in React state so it survives re-renders).
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(defaultHidden ?? []));
  const toggleHidden = (label: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  const MEDIAN_LABEL = "Projected (median)";

  // Theme-aware colors
  const themeColors = {
    title: isDark ? "#E2E8F0" : "#0F172A",
    ticks: isDark ? "#94A3B8" : "#64748B",
    grid: isDark ? "rgba(71, 85, 105, 0.5)" : "rgba(203, 213, 225, 0.5)",
    tooltipBg: isDark ? "rgba(30, 41, 59, 0.95)" : "rgba(15, 23, 42, 0.8)",
    legendText: isDark ? "#CBD5E1" : "#475569",
    // The median-analog projection (line + band) uses the design system's
    // --signature accent, not --primary blue — canvas strokeStyle/fillStyle
    // can't resolve CSS vars, so this mirrors the concrete hex values from
    // tokens.css (same convention as ForwardCurveFanChart's themeColors).
    // Reserved for analog-matching emphasis so it reads as visually distinct
    // from the query ticker's own (blue) price line.
    signature: isDark ? "#FBBF24" : "#F59E0B",
    signatureBand: isDark ? "rgba(251, 191, 36, 0.16)" : "rgba(245, 158, 11, 0.12)",
  };

  // Modern chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: isCompact ? 1.5 : 2,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      // Built-in canvas legend disabled — we render a custom HTML checkbox legend
      // below the chart (real checkboxes, reliable across fonts).
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: data.symbol,
        align: "center" as const,
        font: {
          size: isCompact ? 14 : 16,
          weight: 600,
          family: "Inter, sans-serif",
        },
        padding: {
          top: 10,
          bottom: 15,
        },
        color: themeColors.title,
      },
      tooltip: {
        backgroundColor: themeColors.tooltipBg,
        titleColor: "white",
        bodyColor: "white",
        bodyFont: {
          family: "Inter, sans-serif",
        },
        titleFont: {
          family: "Inter, sans-serif",
          weight: 600,
        },
        padding: 10,
        cornerRadius: 6,
        displayColors: true,
        // Hide the invisible band datasets (blank label) from the tooltip.
        filter: (item: TooltipItem<"line">) => (item.dataset.label ?? "") !== "",
        callbacks: {
          label: function (context: TooltipItem<"line">) {
            const datasetLabel = context.dataset.label || "";
            const value = typeof context.raw === "number" ? context.raw : 0;
            return `${datasetLabel}: $${value.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: !isCompact,
          color: themeColors.grid,
          drawBorder: false,
        },
        ticks: {
          display: !isCompact,
          font: {
            family: "Inter, sans-serif",
            size: 10,
          },
          maxRotation: 45,
          minRotation: 45,
          color: themeColors.ticks,
        },
      },
      y: {
        type: "linear" as const,
        grid: {
          color: themeColors.grid,
          drawBorder: false,
        },
        ticks: {
          font: {
            family: "Inter, sans-serif",
            size: 10,
          },
          color: themeColors.ticks,
          callback: function (value: string | number) {
            const numValue = typeof value === "string" ? parseFloat(value) : value;
            return "$" + numValue.toFixed(2);
          },
        },
      },
    },
    elements: {
      point: {
        radius: isCompact ? 1 : 2,
        hoverRadius: 5,
      },
      line: {
        tension: 0.2,
        borderWidth: isCompact ? 2 : 2.5,
      },
    },
  };

  // Create dataset template
  const createDataset = (index: number, withFill = false): CustomLineData => ({
    label: "",
    fill: withFill ? "origin" : false,
    backgroundColor: withFill
      ? isDark ? "rgba(59, 130, 246, 0.07)" : "rgba(59, 130, 246, 0.05)"
      : undefined,
    borderColor: colors[index % colors.length],
    borderCapStyle: "round",
    borderDash: [],
    borderDashOffset: 0.0,
    borderJoinStyle: "round",
    borderWidth: isCompact ? 2 : 2.5,
    pointBorderColor: colors[index % colors.length],
    pointBackgroundColor: "#fff",
    pointBorderWidth: 1,
    pointHoverRadius: 5,
    pointHoverBackgroundColor: "#fff",
    pointHoverBorderColor: colors[index % colors.length],
    pointHoverBorderWidth: 2,
    pointRadius: isCompact ? 1 : 2,
    pointHitRadius: 10,
    data: [],
  });

  // Prepare chart data
  const lineData: {labels: string[]; datasets: CustomLineData[]} = {
    labels: data.xaxis,
    datasets: [],
  };

  // Main dataset — no under-line fill; the forecast region is shaded instead (below).
  const mainDataset = createDataset(0);
  mainDataset.data = data.yaxis;
  mainDataset.label = data.symbol;
  mainDataset.segment = {
    pointStyle: (ctx: ScriptableLineSegmentContext) => {
      const windowSize = historicalPointCount ?? (data.window || 20);
      return ctx.p0DataIndex >= windowSize - 1 ? "triangle" : "circle";
    },
    borderDash: (ctx: ScriptableLineSegmentContext) => {
      const windowSize = historicalPointCount ?? (data.window || 20);
      return ctx.p0DataIndex >= windowSize - 1 ? [5, 5] : undefined;
    },
  };

  lineData.datasets.push(mainDataset);

  // Median-analog projection cone: a translucent 10th–90th band drawn BEHIND the
  // lines here; the dashed median line is added LAST (on top) below.
  if (dataMedian && dataP10 && dataP90) {
    const bandHidden = hidden.has(MEDIAN_LABEL); // band toggles with the median line
    const p90 = createDataset(0);
    p90.data = dataP90.yaxis;
    p90.label = "";
    p90.hidden = bandHidden;
    p90.borderColor = "transparent";
    p90.pointRadius = 0;
    p90.pointHoverRadius = 0;
    p90.fill = "+1";
    p90.backgroundColor = themeColors.signatureBand;
    const p10 = createDataset(0);
    p10.data = dataP10.yaxis;
    p10.label = "";
    p10.hidden = bandHidden;
    p10.borderColor = "transparent";
    p10.pointRadius = 0;
    p10.pointHoverRadius = 0;
    p10.fill = false;
    lineData.datasets.push(p90, p10);
  }

  // Add forecast datasets if provided
  if (dataUpMax && dataUpAvg && dataDownMax && dataDownAvg && dataAllAvg) {
    const upMaxDataset = createDataset(1);
    upMaxDataset.data = dataUpMax.yaxis;
    upMaxDataset.label = dataUpMax.label;
    upMaxDataset.hidden = hidden.has(dataUpMax.label);
    upMaxDataset.borderDash = [3, 5];
    upMaxDataset.borderColor = colors[1]; // Use success green for up avg
    upMaxDataset.pointBorderColor = colors[1];
    upMaxDataset.pointHoverBorderColor = colors[1];

    const upAvgDataset = createDataset(2);
    upAvgDataset.data = dataUpAvg.yaxis;
    upAvgDataset.label = dataUpAvg.label;
    upAvgDataset.hidden = hidden.has(dataUpAvg.label);
    upAvgDataset.borderDash = [3, 5];
    upAvgDataset.borderColor = colors[2]; // Use success green for up avg
    upAvgDataset.pointBorderColor = colors[2];
    upAvgDataset.pointHoverBorderColor = colors[2];

    const allAvgDataset = createDataset(3);
    allAvgDataset.data = dataAllAvg.yaxis;
    allAvgDataset.label = dataAllAvg.label;
    allAvgDataset.hidden = hidden.has(dataAllAvg.label);
    allAvgDataset.borderDash = [3, 5];
    allAvgDataset.borderColor = colors[3]; // Use info blue for overall avg
    allAvgDataset.pointBorderColor = colors[3];
    allAvgDataset.pointHoverBorderColor = colors[3];

    const downAvgDataset = createDataset(4);
    downAvgDataset.data = dataDownAvg.yaxis;
    downAvgDataset.label = dataDownAvg.label;
    downAvgDataset.hidden = hidden.has(dataDownAvg.label);
    downAvgDataset.borderDash = [3, 5];
    downAvgDataset.borderColor = colors[4]; // Use error red for down avg
    downAvgDataset.pointBorderColor = colors[4];
    downAvgDataset.pointHoverBorderColor = colors[4];

    const downMaxDataset = createDataset(5);
    downMaxDataset.data = dataDownMax.yaxis;
    downMaxDataset.label = dataDownMax.label;
    downMaxDataset.hidden = hidden.has(dataDownMax.label);
    downMaxDataset.borderDash = [3, 5];
    downMaxDataset.borderColor = colors[5]; // Use error red for down max
    downMaxDataset.pointBorderColor = colors[5];
    downMaxDataset.pointHoverBorderColor = colors[5];

    lineData.datasets.push(allAvgDataset);
    lineData.datasets.push(upMaxDataset);
    lineData.datasets.push(upAvgDataset);
    lineData.datasets.push(downAvgDataset);
    lineData.datasets.push(downMaxDataset);
  }

  // Dashed median-analog projection line, on top of everything.
  if (dataMedian && dataP10 && dataP90) {
    const medianDataset = createDataset(0);
    medianDataset.data = dataMedian.yaxis;
    medianDataset.label = dataMedian.label;
    medianDataset.hidden = hidden.has(MEDIAN_LABEL);
    medianDataset.borderColor = themeColors.signature;
    medianDataset.pointBorderColor = themeColors.signature;
    medianDataset.pointHoverBorderColor = themeColors.signature;
    medianDataset.borderDash = [6, 4];
    medianDataset.pointRadius = 0;
    lineData.datasets.push(medianDataset);
  }

  // Custom HTML checkbox legend for the toggleable projection lines (only the ones
  // whose data is present). The query line + band are not listed (band follows the
  // median checkbox).
  const legendItems: {label: string; color: string}[] = [];
  if (dataMedian && dataP10 && dataP90) legendItems.push({label: MEDIAN_LABEL, color: themeColors.signature});
  if (dataAllAvg) legendItems.push({label: dataAllAvg.label, color: colors[3]});
  if (dataUpAvg) legendItems.push({label: dataUpAvg.label, color: colors[2]});
  if (dataDownAvg) legendItems.push({label: dataDownAvg.label, color: colors[4]});
  if (dataUpMax) legendItems.push({label: dataUpMax.label, color: colors[1]});
  if (dataDownMax) legendItems.push({label: dataDownMax.label, color: colors[5]});

  // Faint shade over the forecast region + a dashed "today" divider (mirrors the
  // featured-matches chart). Drawn on the full chart when the labels extend past
  // the window (i.e. there's a forecast region). Self-contained inline plugin.
  const windowSize = historicalPointCount ?? (data.window || 20);
  const xlabels = data.xaxis.length;
  const forecastMarkerPlugin: Plugin<"line"> = {
    id: "forecast-marker",
    beforeDatasetsDraw(chart) {
      if (isCompact || xlabels < 2 || windowSize >= xlabels) return;
      const area = chart.chartArea;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!area) return;
      const frac = (windowSize - 1) / (xlabels - 1);
      const x = area.left + frac * (area.right - area.left);
      if (!Number.isFinite(x)) return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.fillStyle = isDark ? "rgba(59,130,246,0.06)" : "rgba(59,130,246,0.045)";
      ctx.fillRect(x, area.top, area.right - x, area.bottom - area.top);
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = isDark ? "rgba(148,163,184,0.6)" : "rgba(100,116,139,0.5)";
      ctx.moveTo(x, area.top);
      ctx.lineTo(x, area.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = isDark ? "#94A3B8" : "#64748B";
      ctx.font = "600 10px Inter, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("  today · forecast →", x, area.top + 11);
      ctx.restore();
    },
  };

  return (
    <div className={`modern-stock-chart ${isCompact ? "compact" : "full"}`}>
      {!isCompact && legendItems.length > 0 && (
        <div
          className="modern-chart-legend"
          style={{display: "flex", flexWrap: "wrap", gap: "8px 16px", justifyContent: "center", margin: "0 0 8px"}}
        >
          {legendItems.map((item) => (
            <label
              key={item.label}
              style={{display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: themeColors.legendText}}
            >
              <input
                type="checkbox"
                checked={!hidden.has(item.label)}
                onChange={() => toggleHidden(item.label)}
                style={{cursor: "pointer", accentColor: item.color}}
              />
              <span style={{display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: item.color}} />
              {item.label}
            </label>
          ))}
        </div>
      )}
      {/* Fixed-height wrapper so the responsive canvas measures THIS box, not the
          whole component (the legend sibling would otherwise feed a grow loop). */}
      <div style={{position: "relative", height: isCompact ? 200 : 550}}>
        <Line data={lineData} options={options} plugins={[forecastMarkerPlugin]} />
      </div>
    </div>
  );
};

export default ModernStockChart;
